/**
 * gammaInfluence.ts — MER Algo Footprints v7.1B — Phase 6.3
 *
 * Controlled Gamma influence on setup confidence.
 * Pure function — no side effects, no API calls, no state.
 *
 * Placement: src/domains/market/engines/gamma/gammaInfluence.ts
 *
 * ── What this does ────────────────────────────────────────────────────────────
 * Applies small, bounded confidence adjustments to TradeSetup objects based
 * on live Gamma snapshot data. Gamma acts as a MODIFIER only — it cannot
 * change setup type, direction, entry, stop, target, or classification.
 *
 * ── Rules ────────────────────────────────────────────────────────────────────
 * Rule A — Continuation Boost:
 *   NEGATIVE regime + expansion bias + continuation-type setup → small +boost
 *
 * Rule B — Breakout Penalty in Pinning:
 *   POSITIVE regime + PIN bias + breakout/continuation setup → small -penalty
 *
 * Rule C — Mean-Reversion Support:
 *   POSITIVE regime + PIN bias + reversal/fade setup → small +boost
 *
 * ── Hard caps ────────────────────────────────────────────────────────────────
 * - Adjustment is clamped to ±gammaMaxConfidenceAdjustment (default 8)
 * - Confidence is clamped to [0, 100] after adjustment
 * - Stale data halves magnitude (when stale influence is allowed at all)
 * - 'low' data quality → zero influence
 * - gammaScore, pinScore, expansionScore below GAMMA_MIN_SCORE_THRESHOLD → skip
 */

import { TradeSetup } from '../../../../types';
import {
  GammaInfluenceInput,
  GammaInfluenceResult,
  GammaInfluenceAudit,
} from './gammaInfluenceTypes';
import {
  GammaInfluenceFlags,
  GAMMA_INFLUENCE_FLAGS,
  GAMMA_STALE_MAGNITUDE,
  GAMMA_MIN_SCORE_THRESHOLD,
  GAMMA_MIN_DATA_QUALITY,
} from './gammaInfluenceFlags';

// ─── Setup type classifiers ───────────────────────────────────────────────────

/** Returns true if the setup type is continuation-aligned. */
function isContinuationType(setupType: string): boolean {
  const lower = setupType.toLowerCase();
  return (
    lower.includes('continuation') ||
    lower.includes('vwap reclaim') ||
    lower.includes('or breakout') ||
    lower.includes('breakout')
  );
}

/** Returns true if the setup is reversal/fade aligned. */
function isReversalType(setupType: string): boolean {
  const lower = setupType.toLowerCase();
  return (
    lower.includes('reversal') ||
    lower.includes('reversion') ||
    lower.includes('fade') ||
    lower.includes('rejection') ||
    lower.includes('failed breakout') ||
    lower.includes('order block') ||
    lower.includes('fvg demand') ||
    lower.includes('fvg supply') ||
    lower.includes('sweep') ||
    lower.includes('choch') ||
    lower.includes('mean')
  );
}

// ─── Rule implementations ─────────────────────────────────────────────────────

/**
 * Rule A — Continuation Boost.
 * NEGATIVE regime + expansion bias + strong expansion score + continuation setup.
 * Returns the raw adjustment before magnitude scaling.
 */
function ruleA_continuationBoost(
  setup: TradeSetup,
  g: GammaInfluenceInput,
): number | null {
  if (g.regime !== 'NEGATIVE') return null;
  if (g.bias !== 'UPSIDE_EXPANSION' && g.bias !== 'DOWNSIDE_EXPANSION') return null;
  if ((g.expansionScore ?? 0) < GAMMA_MIN_SCORE_THRESHOLD) return null;
  if (!isContinuationType(setup.type)) return null;

  // Directional alignment: expansion bias should match setup direction
  const expandingUp   = g.bias === 'UPSIDE_EXPANSION';
  const expandingDown = g.bias === 'DOWNSIDE_EXPANSION';
  const setupLong     = setup.dir === 'long';
  const setupShort    = setup.dir === 'short';

  if (expandingUp && !setupLong)   return null; // misaligned — skip
  if (expandingDown && !setupShort) return null; // misaligned — skip

  // Scale adjustment with expansion score (55→+4, 80→+6, 100→+8)
  const expansionScore = g.expansionScore ?? 0;
  const raw = Math.round(((expansionScore - GAMMA_MIN_SCORE_THRESHOLD) /
    (100 - GAMMA_MIN_SCORE_THRESHOLD)) * 4 + 4);
  return raw; // positive
}

/**
 * Rule B — Breakout Penalty in Pinning.
 * POSITIVE regime + PIN bias + strong pin score + breakout/continuation setup.
 * Returns the raw adjustment (negative) before magnitude scaling.
 */
function ruleB_breakoutPenalty(
  setup: TradeSetup,
  g: GammaInfluenceInput,
): number | null {
  if (g.regime !== 'POSITIVE') return null;
  if (g.bias !== 'PIN') return null;
  if ((g.pinScore ?? 0) < GAMMA_MIN_SCORE_THRESHOLD) return null;
  if (!isContinuationType(setup.type)) return null;

  // Scale penalty with pin score (55→-3, 80→-5, 100→-7)
  const pinScore = g.pinScore ?? 0;
  const raw = -Math.round(((pinScore - GAMMA_MIN_SCORE_THRESHOLD) /
    (100 - GAMMA_MIN_SCORE_THRESHOLD)) * 4 + 3);
  return raw; // negative
}

/**
 * Rule C — Mean-Reversion Support.
 * POSITIVE regime + PIN bias + reversal/fade setup.
 * Returns the raw adjustment (positive) before magnitude scaling.
 */
function ruleC_meanReversionSupport(
  setup: TradeSetup,
  g: GammaInfluenceInput,
): number | null {
  if (g.regime !== 'POSITIVE') return null;
  if (g.bias !== 'PIN') return null;
  if ((g.pinScore ?? 0) < GAMMA_MIN_SCORE_THRESHOLD) return null;
  if (!isReversalType(setup.type)) return null;

  // Scale boost with pin score (55→+3, 80→+5, 100→+7)
  const pinScore = g.pinScore ?? 0;
  const raw = Math.round(((pinScore - GAMMA_MIN_SCORE_THRESHOLD) /
    (100 - GAMMA_MIN_SCORE_THRESHOLD)) * 4 + 3);
  return raw; // positive
}

// ─── Main influence function ──────────────────────────────────────────────────

/**
 * Applies gamma influence to a single setup, returning the adjusted setup and an audit record.
 * Never mutates the input setup — returns a new object when adjustment is non-zero.
 */
function applyInfluenceToSetup(
  setup:   TradeSetup,
  g:       GammaInfluenceInput,
  flags:   GammaInfluenceFlags,
  symbol:  string,
): GammaInfluenceResult {
  const skipResult = (reason: string): GammaInfluenceResult => ({
    setup, adjustment: 0, rule: 'SKIP', reason,
  });

  // ── Guard: flag off ───────────────────────────────────────────────────────
  if (!flags.gammaScoringEnabled) {
    return skipResult('gammaScoringEnabled=false');
  }

  // ── Guard: unavailable ────────────────────────────────────────────────────
  if (!g.available) {
    return skipResult('gamma unavailable');
  }

  // ── Guard: stale ──────────────────────────────────────────────────────────
  const isStale = g.isStale === true;
  if (isStale && !flags.gammaAllowStaleInfluence) {
    return skipResult('gamma stale and gammaAllowStaleInfluence=false');
  }

  // ── Guard: data quality ───────────────────────────────────────────────────
  const qualityRank = { high: 2, medium: 1, low: 0 } as const;
  const minRank     = qualityRank[GAMMA_MIN_DATA_QUALITY];
  const curRank     = qualityRank[g.dataQuality ?? 'low'] ?? 0;
  if (curRank < minRank) {
    return skipResult(`data quality '${g.dataQuality}' below minimum '${GAMMA_MIN_DATA_QUALITY}'`);
  }

  // ── Try rules in priority order ────────────────────────────────────────────
  type RuleTag = 'A_CONTINUATION_BOOST' | 'B_BREAKOUT_PENALTY' | 'C_MEAN_REVERSION';
  const candidates: Array<{ tag: RuleTag; raw: number | null }> = [
    { tag: 'A_CONTINUATION_BOOST', raw: ruleA_continuationBoost(setup, g) },
    { tag: 'B_BREAKOUT_PENALTY',   raw: ruleB_breakoutPenalty(setup, g)   },
    { tag: 'C_MEAN_REVERSION',     raw: ruleC_meanReversionSupport(setup, g) },
  ];

  const matched = candidates.find(c => c.raw !== null);
  if (!matched) {
    return skipResult('no rule matched');
  }

  let rawAdj = matched.raw!;

  // ── Apply stale magnitude reduction ──────────────────────────────────────
  if (isStale && flags.gammaAllowStaleInfluence) {
    rawAdj = rawAdj * GAMMA_STALE_MAGNITUDE;
  }

  // ── Enforce hard cap ──────────────────────────────────────────────────────
  const cap    = Math.abs(flags.gammaMaxConfidenceAdjustment);
  const adj    = Math.sign(rawAdj) * Math.min(Math.abs(rawAdj), cap);
  const adjRounded = Math.round(adj);

  if (adjRounded === 0) {
    return skipResult('adjustment rounded to zero');
  }

  // ── Apply: return new setup object with clamped confidence ────────────────
  const newConf = Math.max(0, Math.min(100, setup.confidence + adjRounded));
  const adjustedSetup: TradeSetup = { ...setup, confidence: newConf };

  const ruleLabels: Record<RuleTag, string> = {
    A_CONTINUATION_BOOST: 'Rule A: continuation boosted by expansion conditions',
    B_BREAKOUT_PENALTY:   'Rule B: breakout penalized by pinning conditions',
    C_MEAN_REVERSION:     'Rule C: mean-reversion supported by pinning conditions',
  };

  return {
    setup:      adjustedSetup,
    adjustment: adjRounded,
    rule:       matched.tag,
    reason:     ruleLabels[matched.tag],
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface GammaInfluencePassResult {
  setups:  TradeSetup[];
  audits:  GammaInfluenceAudit[];
  applied: number;   // number of setups that received a non-zero adjustment
}

/**
 * Applies gamma influence to all setups in the array.
 * Returns new setup objects where confidence was adjusted; originals where skipped.
 * Produces a full audit record for every setup.
 *
 * @param setups   - output of generateSetups (not mutated)
 * @param g        - live gamma context (from getLiveGammaContext or equivalent)
 * @param symbol   - symbol for audit attribution
 * @param flags    - influence flags (defaults to GAMMA_INFLUENCE_FLAGS)
 */
export function applyGammaInfluencePass(
  setups: TradeSetup[],
  g:      GammaInfluenceInput | null,
  symbol: string,
  flags:  GammaInfluenceFlags = GAMMA_INFLUENCE_FLAGS,
): GammaInfluencePassResult {
  if (!setups.length || !g) {
    return { setups, audits: [], applied: 0 };
  }

  const resultSetups: TradeSetup[]         = [];
  const audits:       GammaInfluenceAudit[] = [];
  let   applied = 0;

  for (const setup of setups) {
    const result = applyInfluenceToSetup(setup, g, flags, symbol);
    resultSetups.push(result.setup);

    const audit: GammaInfluenceAudit = {
      symbol,
      setupType:        setup.type,
      setupDir:         setup.dir,
      flagState:        flags.gammaScoringEnabled,
      gammaAvailable:   g.available,
      gammaStale:       g.isStale ?? null,
      regime:           g.regime,
      bias:             g.bias,
      pinScore:         g.pinScore,
      expansionScore:   g.expansionScore,
      directionalScore: g.directionalScore,
      dataQuality:      g.dataQuality,
      priorConfidence:  setup.confidence,
      adjustment:       result.adjustment,
      resultConfidence: result.setup.confidence,
      rule:             result.rule,
      reason:           result.reason,
    };
    audits.push(audit);
    if (result.adjustment !== 0) applied++;
  }

  return { setups: resultSetups, audits, applied };
}

// Re-export types for consumers
export type { GammaInfluenceInput, GammaInfluenceResult, GammaInfluenceAudit };
